import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, TrendingUp, Users, UserCheck, UserX, Sparkles, Megaphone, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cardStyle = {
  background: '#FFFFFF',
  borderRadius: 18,
  padding: 14,
  border: '1px solid rgba(112, 63, 246, 0.08)',
  boxShadow: 'var(--premium-shadow-nested)',
  display: 'grid',
  gap: 12,
};

const metricTileStyle = {
  display: 'grid',
  gap: 4,
  padding: '10px 12px',
  borderRadius: 14,
  background: '#FAFAFA',
  border: '1px solid #EFEFEF',
  minWidth: 140,
  flex: 1,
};

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const ProgressBar = ({ percent, color }) => (
  <div style={{ height: 8, borderRadius: 999, background: '#F0F0F0', overflow: 'hidden' }}>
    <div
      style={{
        width: `${Math.min(100, Math.max(0, percent))}%`,
        height: '100%',
        background: color,
        borderRadius: 999,
        transition: 'width 280ms ease',
      }}
    />
  </div>
);

const Pill = ({ children, tone = 'neutral', testId }) => {
  const palette =
    tone === 'good'
      ? { bg: '#EAF7EE', text: '#0F6B19', border: '#BCE6C8' }
      : tone === 'warn'
      ? { bg: '#FFF4E0', text: '#A36500', border: '#F4D9A8' }
      : { bg: '#F4F4F4', text: '#555', border: '#E6E6E6' };
  return (
    <span
      data-testid={testId}
      style={{
        fontSize: 11,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 9999,
        background: palette.bg,
        color: palette.text,
        border: `1px solid ${palette.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
};

const HostRow = ({ rank, name, email, right, rightSub, testId, accent }) => (
  <div
    data-testid={testId}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '8px 6px',
      borderTop: '1px solid #F2F2F2',
    }}
  >
    {rank ? (
      <span
        style={{
          width: 22,
          height: 22,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 9999,
          background: accent === 'warn' ? '#FFF4E0' : '#F6F4FB',
          color: accent === 'warn' ? '#A36500' : 'var(--accent-purple-strong)',
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        {rank}
      </span>
    ) : null}
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</p>
      <p style={{ margin: '2px 0 0', fontSize: 11, color: '#777', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</p>
    </div>
    <div style={{ textAlign: 'right' }}>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111' }}>{right}</p>
      {rightSub ? <p style={{ margin: '2px 0 0', fontSize: 11, color: '#777' }}>{rightSub}</p> : null}
    </div>
  </div>
);

export const HostInvitesActivity = ({ headers, onSelectHost }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudgeModalOpen, setNudgeModalOpen] = useState(false);
  const [nudging, setNudging] = useState(false);

  const load = async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics/host-invites`, { headers });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load host invite activity');
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('host-invites load failed', err);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers]);

  const summary = data?.summary || {};
  const windows = data?.windows || {};
  const topInviters = data?.top_inviters || [];
  const silentHosts = data?.silent_hosts || [];
  const eligibleCount = summary.nudge_eligible_count || 0;
  const cooldownDays = summary.nudge_cooldown_days || 10;
  const totalSilent = summary.hosts_silent || 0;

  const handleConfirmNudge = async () => {
    setNudging(true);
    try {
      const res = await axios.post(
        `${API}/admin/analytics/host-invites/nudge-silent`,
        {},
        { headers },
      );
      const { nudged_count: nudged, cooldown_skipped: skipped } = res.data || {};
      if (nudged > 0) {
        toast.success(
          `Push sent to ${nudged} silent host${nudged === 1 ? '' : 's'}${
            skipped ? ` · ${skipped} skipped (cooldown)` : ''
          }`,
        );
      } else if (skipped > 0) {
        toast.message(`All ${skipped} silent host${skipped === 1 ? '' : 's'} still in ${cooldownDays}-day cooldown`);
      } else {
        toast.message('No silent hosts to nudge right now');
      }
      setNudgeModalOpen(false);
      await load({ silent: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Nudge failed';
      toast.error(detail);
    } finally {
      setNudging(false);
    }
  };

  return (
    <div data-testid="admin-host-invites-activity" style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Send size={20} color="var(--accent-purple-strong)" />
        <div style={{ flex: 1 }}>
          <h3 style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, margin: 0, color: '#000' }}>
            Host invite activity
          </h3>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#777' }}>
            Are hosts actually inviting other hosts? Use this to decide when the next tier should unlock.
          </p>
        </div>
        {summary.invitation_rate_percent != null ? (
          <Pill
            tone={summary.invitation_rate_percent >= 30 ? 'good' : 'warn'}
            testId="admin-host-invites-rate-pill"
          >
            {formatPercent(summary.invitation_rate_percent)} inviting
          </Pill>
        ) : null}
      </div>

      {loading ? (
        <p style={{ textAlign: 'center', color: '#aaa', fontSize: 13, padding: 12 }}>Loading…</p>
      ) : (
        <>
          {/* Headline summary tiles */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <div style={metricTileStyle} data-testid="admin-host-invites-tile-active">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <UserCheck size={14} color="#0F6B19" />
                <span style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                  Inviting hosts
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{summary.hosts_who_invited || 0}</p>
              <p style={{ margin: 0, fontSize: 11, color: '#888' }}>of {summary.total_hosts || 0} total</p>
              <ProgressBar percent={summary.invitation_rate_percent || 0} color="#04A628" />
            </div>

            <div style={metricTileStyle} data-testid="admin-host-invites-tile-silent">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <UserX size={14} color="#A36500" />
                <span style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                  Silent hosts
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{summary.hosts_silent || 0}</p>
              <p style={{ margin: 0, fontSize: 11, color: '#888' }}>haven't invited anyone</p>
            </div>

            <div style={metricTileStyle} data-testid="admin-host-invites-tile-conversion">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <TrendingUp size={14} color="#2F79EA" />
                <span style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                  Invite → join rate
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{formatPercent(summary.conversion_percent || 0)}</p>
              <p style={{ margin: 0, fontSize: 11, color: '#888' }}>
                {summary.invites_joined || 0} joined / {summary.total_invites_sent || 0} sent
              </p>
            </div>

            <div style={metricTileStyle} data-testid="admin-host-invites-tile-unlocked">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Sparkles size={14} color="#7A4FE0" />
                <span style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                  Hosts on 2% tier
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{summary.hosts_unlocked_2pct || 0}</p>
              <p style={{ margin: 0, fontSize: 11, color: '#888' }}>unlocked via referrals</p>
            </div>
          </div>

          {/* Rolling windows */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ ...metricTileStyle, background: '#F8F5FF', borderColor: '#E5DAFF' }} data-testid="admin-host-invites-window-7d">
              <span style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                Last 7 days
              </span>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111' }}>
                {windows.last_7_days?.invites_sent || 0} sent · {windows.last_7_days?.invites_joined || 0} joined
              </p>
            </div>
            <div style={{ ...metricTileStyle, background: '#F8F5FF', borderColor: '#E5DAFF' }} data-testid="admin-host-invites-window-30d">
              <span style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                Last 30 days
              </span>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111' }}>
                {windows.last_30_days?.invites_sent || 0} sent · {windows.last_30_days?.invites_joined || 0} joined
              </p>
            </div>
          </div>

          {/* Top inviters & silent hosts side-by-side */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <Users size={15} color="#04A628" />
                <h4 style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, margin: 0, color: '#000' }}>Top inviters (30d)</h4>
              </div>
              {topInviters.length === 0 ? (
                <p style={{ margin: '8px 0', fontSize: 12, color: '#aaa', textAlign: 'center' }}>No invite activity in the last 30 days</p>
              ) : (
                topInviters.map((host, idx) => (
                  <HostRow
                    key={host.host_id}
                    testId={`admin-host-invites-top-${host.host_id}`}
                    rank={idx + 1}
                    name={host.host_name}
                    email={host.host_email}
                    right={`${host.invites_sent} sent`}
                    rightSub={`${host.invites_joined} joined · ${Number(host.commission_rate).toFixed(1)}%`}
                  />
                ))
              )}
            </div>

            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <UserX size={15} color="#A36500" />
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, margin: 0, color: '#000' }}>Silent base-tier hosts</h4>
                  <p style={{ margin: '1px 0 0', fontSize: 10.5, color: '#888' }}>
                    Hosts who've been on the 0.5% tier for 14+ days without sending a single invite.
                  </p>
                </div>
                {totalSilent > 0 ? (
                  <button
                    type="button"
                    data-testid="admin-host-invites-nudge-btn"
                    onClick={() => setNudgeModalOpen(true)}
                    disabled={eligibleCount === 0}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 12px',
                      borderRadius: 9999,
                      border: '1px solid var(--accent-purple-strong)',
                      background: eligibleCount === 0 ? '#F4F4F4' : 'var(--accent-purple-strong)',
                      color: eligibleCount === 0 ? '#888' : '#FFF',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: eligibleCount === 0 ? 'not-allowed' : 'pointer',
                      opacity: eligibleCount === 0 ? 0.7 : 1,
                      transition: 'opacity 150ms ease, transform 150ms ease',
                    }}
                    title={
                      eligibleCount === 0
                        ? `All silent hosts are still in the ${cooldownDays}-day cooldown window`
                        : `Send a push to ${eligibleCount} silent host${eligibleCount === 1 ? '' : 's'}`
                    }
                  >
                    <Megaphone size={13} />
                    {eligibleCount === 0
                      ? 'All cooling down'
                      : `Nudge ${eligibleCount} silent host${eligibleCount === 1 ? '' : 's'}`}
                  </button>
                ) : null}
              </div>
              {silentHosts.length === 0 ? (
                <p style={{ margin: '8px 0', fontSize: 12, color: '#aaa', textAlign: 'center' }}>Everyone has invited at least one — nice!</p>
              ) : (
                silentHosts.map((host, idx) => (
                  <HostRow
                    key={host.host_id}
                    testId={`admin-host-invites-silent-${host.host_id}`}
                    rank={idx + 1}
                    accent="warn"
                    name={host.host_name}
                    email={host.host_email}
                    right={
                      host.in_cooldown ? (
                        <span
                          data-testid={`admin-host-invites-silent-${host.host_id}-cooldown`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#A36500',
                            background: '#FFF4E0',
                            border: '1px solid #F4D9A8',
                            padding: '2px 8px',
                            borderRadius: 9999,
                          }}
                        >
                          <Clock size={11} /> Nudged
                        </span>
                      ) : (host.days_as_host ?? host.days_since_signup) != null ? (
                        `${host.days_as_host ?? host.days_since_signup}d`
                      ) : (
                        '—'
                      )
                    }
                    rightSub={host.in_cooldown ? 'cooling down' : 'as host'}
                  />
                ))
              )}
            </div>
          </div>
        </>
      )}

      {nudgeModalOpen ? (
        <div
          data-testid="admin-host-invites-nudge-modal"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 16,
          }}
          onClick={() => !nudging && setNudgeModalOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 460,
              width: '100%',
              background: '#FFF',
              borderRadius: 22,
              padding: 22,
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              display: 'grid',
              gap: 14,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 12,
                  background: '#F6F4FB',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Megaphone size={20} color="var(--accent-purple-strong)" />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, margin: 0, color: '#000' }}>
                  Nudge silent hosts?
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#777' }}>
                  Push notification only · {cooldownDays}-day cooldown per host
                </p>
              </div>
            </div>

            <div
              style={{
                background: '#FAFAFA',
                border: '1px solid #EFEFEF',
                borderRadius: 14,
                padding: 12,
                display: 'grid',
                gap: 8,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: '#666' }}>Eligible for nudge</span>
                <span
                  data-testid="admin-host-invites-nudge-modal-eligible"
                  style={{ fontWeight: 700, color: '#0F6B19' }}
                >
                  {eligibleCount}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: '#666' }}>In cooldown (skipped)</span>
                <span style={{ fontWeight: 700, color: '#A36500' }}>
                  {Math.max(0, totalSilent - eligibleCount)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: '#666' }}>Total silent hosts</span>
                <span style={{ fontWeight: 700, color: '#111' }}>{totalSilent}</span>
              </div>
            </div>

            <div
              style={{
                background: '#F8F5FF',
                border: '1px solid #E5DAFF',
                borderRadius: 14,
                padding: '10px 12px',
                fontSize: 12,
                color: '#444',
                lineHeight: 1.5,
              }}
            >
              <strong style={{ color: 'var(--accent-purple-strong)' }}>Message preview:</strong>
              <br />
              <em>"Invite a fellow host to Allure — hosts who refer others unlock the 2% tier, and a hidden next step is coming soon."</em>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                data-testid="admin-host-invites-nudge-cancel"
                onClick={() => setNudgeModalOpen(false)}
                disabled={nudging}
                style={{
                  padding: '8px 14px',
                  borderRadius: 9999,
                  border: '1px solid #E0E0E0',
                  background: '#FFF',
                  color: '#333',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: nudging ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="admin-host-invites-nudge-confirm"
                onClick={handleConfirmNudge}
                disabled={nudging || eligibleCount === 0}
                style={{
                  padding: '8px 16px',
                  borderRadius: 9999,
                  border: 'none',
                  background: 'var(--accent-purple-strong)',
                  color: '#FFF',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: nudging || eligibleCount === 0 ? 'not-allowed' : 'pointer',
                  opacity: nudging || eligibleCount === 0 ? 0.7 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Megaphone size={14} />
                {nudging ? 'Sending…' : `Send ${eligibleCount} push${eligibleCount === 1 ? '' : 'es'}`}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default HostInvitesActivity;
